<template>
  <div class="download-container">
    <div class="download-box">
      <div v-if="loading" class="loading-state">
        <h2>Preparing your download...</h2>
        <p>Please wait while we fetch your file.</p>
      </div>

      <div v-if="error" class="error-state">
        <h2>Download Unavailable</h2>
        <p>{{ errorMessage }}</p>
        <p class="error-details">This link may have expired or is invalid.</p>
      </div>

      <div v-if="success" class="success-state">
        <h2>Download Started</h2>
        <p>Your download should begin automatically.</p>
        <p class="success-details">If it doesn't start, <a :href="downloadUrl" target="_blank">click here</a>.</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { useRoute } from "vue-router";
import { logger } from "../utils/logger";

const route = useRoute();
const loading = ref(true);
const error = ref(false);
const success = ref(false);
const errorMessage = ref("");
const downloadUrl = ref("");

onMounted(() => {
  const fiNumber = route.params.fiNumber;
  const shortKey = route.params.shortKey;

  logger.debug("ShareableDownload mounted", { fiNumber, shortKey });

  if (!fiNumber || !shortKey) {
    error.value = true;
    errorMessage.value = "Invalid download link";
    loading.value = false;
    return;
  }

  // The shareable endpoint is unauthenticated, so hit the API host directly rather than
  // relying on the website's nginx /v1 proxy. iOS itms-services is handled outside the
  // browser and needs an absolute URL anyway.
  const apiUrl = process.env.VUE_APP_API_URL;
  if (!apiUrl) {
    logger.error("VUE_APP_API_URL is not set; cannot build shareable download URL");
    error.value = true;
    errorMessage.value = "Configuration error";
    loading.value = false;
    return;
  }
  const shareableUrl = `${apiUrl}/v1/s/${fiNumber}/${shortKey}`;

  logger.debug("Shareable URL:", shareableUrl);

  // Detect if user is on iOS device
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  const isIOS = /iPhone|iPad|iPod/i.test(userAgent);

  logger.debug("Device detection:", { isIOS, userAgent });

  downloadUrl.value = shareableUrl;
  loading.value = false;
  success.value = true;

  // If iOS device, trigger OTA installation via itms-services
  // iOS will then request the manifest from the backend
  if (isIOS) {
    logger.debug("iOS device detected, triggering itms-services installation");

    // Encode URL for itms-services (only special chars, not : and /)
    const encodedUrl = shareableUrl.replace(/&/g, '%26').replace(/\?/g, '%3F').replace(/#/g, '%23');
    const installUrl = `itms-services://?action=download-manifest&url=${encodedUrl}`;

    logger.debug("Install URL:", installUrl);

    // Trigger iOS OTA installation
    window.location.href = installUrl;
  } else {
    // For non-iOS devices, redirect to the backend API endpoint
    // Backend will detect it's not an iOS OTA request and redirect to S3 for download
    logger.debug("Non-iOS device, redirecting to download URL");
    window.location.href = shareableUrl;
  }
});
</script>

<style scoped>
.download-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  background-color: #fdf8ff;
  width: 100%;
  padding: 20px;
}

.download-box {
  background: white;
  padding: 40px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  text-align: center;
  max-width: 500px;
  color: #404040;
}

.loading-state h2,
.error-state h2,
.success-state h2 {
  color: #594bab;
  margin-bottom: 20px;
}

.error-state h2 {
  color: #d32f2f;
}

.success-state h2 {
  color: #388e3c;
}

.error-details,
.success-details {
  font-size: 14px;
  color: #666;
  margin-top: 10px;
}

.success-details a {
  color: #594bab;
  text-decoration: underline;
}

p {
  margin: 10px 0;
  line-height: 1.5;
}
</style>
